import { Bar, BarChart, LabelList, Text as RechartsText, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Spinner } from '@zillow/constellation-10';

import { RentalManagerFreemiumAnalyticsFilterType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_inquiries,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_overview,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PropertyConstants from '../../../constants/PropertyConstants';

type Props = {
  listingAlias: string;
};

// Matches BEDROOMS_API_CONSTANTS in legacy PremiumConstants: 4 beds → '4+'
const getBedroomsParam = (beds: number): string => (beds >= 4 ? '4+' : beds.toString());

const YOUR_INQUIRIES = 'Your inquiries';
const AVG_INQUIRIES = 'Average inquiries';

const getDomainBuffer = (propertyCount: number, avgCount: number) => {
  const max = Math.max(propertyCount, avgCount);
  if (max > 999) return 1000;
  if (max > 99) return 300;
  return 100;
};

// @ts-expect-error recharts tick props are not typed
const renderYAxisTick = ({ y, payload: { value } }) => {
  const adjustment = value === YOUR_INQUIRIES ? 4 : -13;
  return (
    <g transform={`translate(0,${y + adjustment})`}>
      <RechartsText fontSize="14px" fill="#2A2A33" x={0} y={8} textAnchor="start">
        {value}
      </RechartsText>
    </g>
  );
};

const PremiumStatistics = ({ listingAlias }: Props) => {
  const { data: overviewData, isLoading: overviewLoading } =
    useRentalManagerUserFreemiumAnalyticsWebApiHandler_overview(
      {
        listingAlias,
        filterType: RentalManagerFreemiumAnalyticsFilterType.CITY_STATE,
      },
      {
        enabled: Boolean(listingAlias),
        meta: {
          tags: ['premiumStatisticsOverview'],
          context: { api: 'freemiumAnalytics', details: 'PremiumStatistics' },
        },
      },
    );

  const { data: inquiriesData, isLoading: inquiriesLoading } =
    useRentalManagerUserFreemiumAnalyticsWebApiHandler_inquiries(
      {
        listingAlias,
        filterType: RentalManagerFreemiumAnalyticsFilterType.CITY_STATE,
        bedrooms: overviewData?.beds != null ? getBedroomsParam(overviewData.beds) : undefined,
        propertyType:
          overviewData?.propertyType?.code != null
            ? PropertyConstants.PROPERTY_CODE_TO_ENUM[overviewData.propertyType.code]
            : undefined,
      },
      {
        enabled: Boolean(overviewData),
        meta: {
          tags: ['premiumStatisticsInquiries'],
          context: { api: 'freemiumAnalytics', details: 'PremiumStatistics' },
        },
      },
    );

  if (overviewLoading || inquiriesLoading) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (!inquiriesData) {
    return null;
  }

  const propertyCount = inquiriesData.property.countSinceActivation;
  const avgCount = inquiriesData.marketAverage.countSinceActivation;
  const domainBuffer = getDomainBuffer(propertyCount, avgCount);

  const chartData = [
    { name: YOUR_INQUIRIES, property: propertyCount },
    { name: AVG_INQUIRIES, average: avgCount },
  ];

  return (
    <Box className="PremiumStatistics">
      <ResponsiveContainer debounce={200} width="99%" height={100}>
        <BarChart data={chartData} layout="vertical">
          <XAxis type="number" hide={true} domain={[0, `dataMax + ${domainBuffer}`]} />
          <YAxis width={120} type="category" dataKey="name" tickLine={false} minTickGap={1} tick={renderYAxisTick} />
          <Bar isAnimationActive={false} dataKey="average" fill="#6000b6" barSize={16} minPointSize={1}>
            <LabelList fontWeight={700} dataKey="average" position="right" />
          </Bar>
          <Bar isAnimationActive={false} dataKey="property" fill="#00c3dc" barSize={16} minPointSize={1}>
            <LabelList fontWeight={700} dataKey="property" position="right" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PremiumStatistics;
